import { StoreService } from "@/services/StoreService";
import { RealtimeDatabase } from "./RealtimeDatabase";
import { Analytics } from "./Analytics";

class UserProfileServiceClass {
  getUserProfileData() {
    const userProfileData = StoreService.getUserState().profileData;
    return userProfileData;
  }

  addSubject({ id, title }: { id: string; title: string }) {
    const activeUser = StoreService.getActiveUser();

    if (activeUser) {
      RealtimeDatabase.addCurrentUserSubject({
        subjectId: id,
        subjectData: {
          id,
          title,
        },
      });
      return;
    }

    StoreService.createStateUpdater(
      (draft) => {
        if (!draft.user.profileData) {
          draft.user.profileData = {
            subjects: {},
            industries: {},
            jobFunctions: {},
            traits: {},
            companies: {},
          };
        }

        draft.user.profileData.subjects = {
          ...draft.user.profileData.subjects,
          [id]: {
            id,
            title,
          },
        };
      },
      { saveLocal: true },
    );
  }

  removeSubject({ id }: { id: string }) {
    const activeUser = StoreService.getActiveUser();

    if (activeUser) {
      RealtimeDatabase.removeCurrentUserSubject({ subjectId: id });
      return;
    }

    StoreService.createStateUpdater(
      (draft) => {
        delete draft.user.profileData.subjects[id];
      },
      { saveLocal: true },
    );
  }

  clearSubjects() {
    const activeUser = StoreService.getActiveUser();
    if (activeUser) {
      RealtimeDatabase.clearCurrentUserSubjects();
      return;
    }
  }

  clearIndustries() {
    const activeUser = StoreService.getActiveUser();
    if (activeUser) {
      RealtimeDatabase.clearCurrentUserIndustries();
      return;
    }
  }

  addIndustry({ id, title }: { id: string; title: string }) {
    const activeUser = StoreService.getActiveUser();

    if (activeUser) {
      RealtimeDatabase.addCurrentUserIndustry({
        industryId: id,
        industryData: {
          id,
          title,
        },
      });
      return;
    }

    StoreService.createStateUpdater(
      (draft) => {
        draft.user.profileData.industries = {
          ...draft.user.profileData.industries,
          [id]: {
            id,
            title,
          },
        };
      },
      { saveLocal: true },
    );
  }

  removeIndustry({ id }: { id: string }) {
    const activeUser = StoreService.getActiveUser();

    if (activeUser) {
      RealtimeDatabase.removeCurrentUserIndustry({ industryId: id });
      return;
    }

    StoreService.createStateUpdater(
      (draft) => {
        delete draft.user.profileData.industries[id];
      },
      { saveLocal: true },
    );
  }

  getUserSubjects() {
    return this.getUserProfileData()?.subjects || {};
  }

  getUserHasSelectedSubjects() {
    return Object.keys(this.getUserSubjects()).length > 0;
  }

  getUserIndustries() {
    return this.getUserProfileData()?.industries || {};
  }

  getUserHasSelectedIndustries() {
    return Object.keys(this.getUserIndustries()).length > 0;
  }

  updateUserProfileAndPlaylistDataFromStore() {
    const userProfileData = StoreService.getUserProfileData();
    const userPlaylistData = StoreService.getUserPlaylistData();

    RealtimeDatabase.updateCurrentUser({
      profileData: userProfileData || {},
      playlistData: userPlaylistData || {},
    });
  }

  updateUserName({ name }: { name: string }) {
    const activeUser = StoreService.getActiveUser();
    if (activeUser) {
      RealtimeDatabase.updateCurrentUser({ name });
      Analytics.updateUserName({ label: name });
      return;
    }
  }

  updateVideoReaction({
    vimeoId,
    reaction,
    questionNumber,
    advizerId,
  }: {
    vimeoId: string;
    reaction: string;
    questionNumber: number;
    advizerId: string;
  }) {
    const activeUser = StoreService.getActiveUser();

    if (activeUser) {
      RealtimeDatabase.updateCurrentUserVideoReaction({
        vimeoId,
        reaction,
        questionNumber,
        advizerId,
      });
      return;
    }
  }

  updateUserRecommendedAdvizers(advizerIds: string[]) {
    const activeUser = StoreService.getActiveUser();
    if (activeUser) {
      RealtimeDatabase.updateCurrentUserRecommendedAdvizers({ advizerIds });
      return;
    }
  }
}

export const UserProfileService = new UserProfileServiceClass();
